import React from 'react';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Hero } from 'src/components/Hero';
import { HeroHeader } from 'src/components/HeroHeader';
import { ContentBlock } from 'src/pages/hubs/LearningPathPage/children/ContentBlock';
import { getImageAttrs } from 'src/utils/contentful';
import { LearningPathsHubLoaderProps } from 'src/pages/hubs/LearningPathPage/loader';

interface HubLayoutProps {
  hubData: LearningPathsHubLoaderProps['learningPathsHubData'];
  // If true, the page has a subnav (not rendered by this component) - account for margin top
  hasSubnav?: boolean;
}

const renderHero = ({
  showFullHero,
  titleExternal,
  subtitle,
  eyebrowText,
  backgroundImagePath,
}: {
  showFullHero?: boolean;
  titleExternal: string;
  subtitle: string;
  eyebrowText: string;
  backgroundImagePath?: string;
}): React.ReactNode => {
  return showFullHero ? (
    <HeroHeader heading={titleExternal} subtext={subtitle} backgroundImagePath={backgroundImagePath} />
  ) : (
    <Hero title={titleExternal} subtitle={subtitle} eyebrowText={eyebrowText} showHeroDesktopImage={true} />
  );
};

export const HubLayout: React.FC<HubLayoutProps> = ({ hubData, hasSubnav = true }) => {
  return (
    <main
      data-testid="learning-paths-hub"
      css={[pageContainerStylesBase, hasSubnav ? pageContainerStylesWithSubnav : pageContainerStylesWithoutSubnav]}
    >
      {renderHero(hubData.hero)}
      <div data-testid="learning-paths-content-section" css={pageContentContainerStyles}>
        {!!hubData.linkedContentBlockCollection.items.length &&
          hubData.linkedContentBlockCollection.items.map((contentBlock) => (
            <ContentBlock
              key={contentBlock.sys.id}
              imageSrc={getImageAttrs(contentBlock.asset)?.src}
              title={contentBlock.titleExternal}
              subtitle={contentBlock.subtitle}
              associatedContent={contentBlock.associatedContentResourcesCollection.items}
            />
          ))}
      </div>
    </main>
  );
};

const pageContainerStylesBase = css({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',

  '@media (max-width: 1279px)': {
    marginTop: '0',
  },
});

const pageContainerStylesWithSubnav = css({
  '@media (min-width: 1280px)': {
    marginTop: '136px',
  },
});

const pageContainerStylesWithoutSubnav = css({
  '@media (min-width: 1280px)': {
    marginTop: '68px',
  },
});

const pageContentContainerStyles = css({
  width: '100%',
  height: '100%',
  maxWidth: '1022px',
  display: 'flex',
  padding: `0 ${token('space.300')}`,
  flexDirection: 'column',
  alignItems: 'flex-start',

  [media.below.xs]: {
    padding: `0 ${token('space.200')}`,
  },
});
